import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/http';
import { Notice, errorMessage } from '../../components/common';
import { t } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface HostelView {
  currency: string;
  bed: {
    hostelName: string;
    roomNumber: string;
    floor: number | null;
    wardenName: string | null;
    monthlyFeeMinor: number;
  } | null;
  outpasses: {
    id: string;
    leavesOn: string;
    expectedBack: string;
    returnedOn: string | null;
    reason: string;
    status: 'out' | 'returned';
    lateDays: number;
  }[];
  charges: { period: string; amountMinor: number; invoiceNumber: string; invoiceStatus: string }[];
}

/** A parent's view of their child's hostel: room, warden, out-passes, and what has been billed. */
export function ChildHostel({ studentId }: { studentId: string }) {
  const view = useQuery({
    queryKey: ['me', 'children', studentId, 'hostel'],
    queryFn: () => api<HostelView>(`/me/children/${studentId}/hostel`),
  });
  if (view.isPending) return <p role="status">{t('common.loading')}</p>;
  if (view.isError) return <Notice kind="error">{errorMessage(view.error)}</Notice>;
  const { bed, outpasses, charges, currency } = view.data;
  return (
    <div className="space-y-4">
      {!bed && <p className="text-slate-600">{t('ho.noBed')}</p>}
      {bed && (
        <section
          aria-label={t('ho.myBed')}
          className="space-y-1 rounded-lg border border-slate-200 bg-white p-4 text-sm"
        >
          <h2 className="text-lg font-semibold">{bed.hostelName}</h2>
          <p>
            {t('ho.roomLabel', { number: bed.roomNumber })}
            {bed.floor !== null ? ` · ${t('ho.floorLabel', { floor: bed.floor })}` : ''}
          </p>
          {bed.wardenName && <p>{t('ho.wardenIs', { name: bed.wardenName })}</p>}
          <p>{t('ho.perMonth', { amount: formatMinor(bed.monthlyFeeMinor, currency) })}</p>
        </section>
      )}
      <section aria-label={t('ho.passes')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('ho.passes')}</h2>
        {outpasses.length === 0 && <p className="text-slate-600">{t('ho.noPasses')}</p>}
        <ul className="space-y-2 text-sm">
          {outpasses.map((o) => (
            <li key={o.id} className="rounded-lg border border-slate-200 bg-white p-3">
              {o.leavesOn} → {o.returnedOn ?? o.expectedBack} · {o.reason}
              {o.status === 'out' ? ` (${t('ho.statusOut')})` : ''}
              {o.lateDays > 0 && (
                <span className="ml-2 font-medium text-red-700">
                  {t('ho.lateDays', { days: o.lateDays })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section aria-label={t('ho.billedMonths')} className="space-y-2">
        <h2 className="text-lg font-semibold">{t('ho.billedMonths')}</h2>
        {charges.length === 0 && <p className="text-slate-600">{t('ho.noCharges')}</p>}
        <ul className="space-y-2 text-sm">
          {charges.map((c) => (
            <li key={c.period} className="rounded-lg border border-slate-200 bg-white p-3">
              {c.period} · <strong>{formatMinor(c.amountMinor, currency)}</strong> ·{' '}
              {c.invoiceNumber}
              {c.invoiceStatus === 'cancelled' ? ` (${t('ho.cancelled')})` : ''}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
